import { X, User, Car, FileText, MapPin, Phone, Mail, CreditCard, Receipt } from 'lucide-react'
import { formatCurrency, formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import { ContratoExpanded } from '@/services/contrato.service'

interface Props {
    contrato: ContratoExpanded | null
    onClose: () => void
}

export function ContratoDetailDrawer({ contrato, onClose }: Props) {
    if (!contrato) return null
    const c = contrato as any // usa any para acessar campos extras do JOIN

    const assoc = c.associado || {}
    const veic = c.veiculo || {}

    return (
        <div className="fixed inset-0 z-50 flex justify-end animate-fade-in">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-2xl bg-white shadow-2xl h-full overflow-y-auto flex flex-col">

                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-surface-100 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <h3 className="text-lg font-bold text-surface-900">Detalhes do Contrato</h3>
                        <p className="text-xs text-surface-700/50 font-mono">{c.external_reference || c.id}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`badge ${getStatusBadgeClass(c.status)}`}>{translateStatus(c.status)}</span>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-700 transition-colors">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 p-6 space-y-6">

                    {/* ── Resumo financeiro ── */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <FinCard label="Mensalidade" value={formatCurrency(c.ultima_fatura_valor ?? c.valor_total_mensal)} highlight />
                        <FinCard label="Adesão" value={formatCurrency(c.valor_adesao)} />
                        <FinCard label="Tx Admin" value={formatCurrency(c.valor_tx_admin)} />
                        <FinCard label="Vistoria" value={formatCurrency(c.valor_vistoria)} />
                    </div>

                    {/* ── Dados do Contrato ── */}
                    <Section title="Contrato" icon={FileText}>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                            <Field label="Plano" value={c.plano} />
                            <Field label="Chave PDA" value={c.external_reference} mono />
                            <Field label="Data de Início" value={formatDate(c.data_inicio)} />
                            <Field label="Dia de Vencimento" value={`Dia ${c.dia_vencimento}`} />
                            <Field label="Ciclo" value={c.ciclo_faturamento} />
                            <Field label="Tipo de Pagamento" value={c.tipo_pagamento} />
                            {c.desconto && <Field label="Desconto" value={formatCurrency(c.desconto)} />}
                            {c.justificativa && <Field label="Justificativa desconto" value={c.justificativa} span={2} />}
                            {c.obs && <Field label="Observações" value={c.obs} span={2} />}
                            {c.st && <Field label="ST" value={c.st} />}
                            {c.indice && <Field label="Índice" value={String(c.indice)} />}
                        </div>
                    </Section>

                    {/* ── Associado ── */}
                    <Section title="Associado" icon={User}>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                            <Field label="Nome" value={assoc.nome || c.associado_nome} span={2} />
                            <Field label="CPF" value={assoc.cpf || c.associado_cpf} mono />
                            <Field label="Data de Nasc." value={assoc.data_nascimento ? formatDate(assoc.data_nascimento) : undefined} />
                            <Field label="Telefone" value={assoc.telefone || c.associado_telefone} icon={Phone} />
                            <Field label="Celular" value={assoc.celular} icon={Phone} />
                            <Field label="E-mail" value={assoc.email} icon={Mail} span={2} />
                            {assoc.logradouro && (
                                <Field
                                    label="Endereço"
                                    icon={MapPin}
                                    span={2}
                                    value={[assoc.logradouro, assoc.numero, assoc.complemento, assoc.bairro, assoc.cidade, assoc.uf].filter(Boolean).join(', ')}
                                />
                            )}
                            {assoc.cep && <Field label="CEP" value={assoc.cep} mono />}
                        </div>
                    </Section>

                    {/* ── Veículo ── */}
                    {(c.veiculo_placa || veic.placa) && (
                        <Section title="Veículo" icon={Car}>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                                <Field label="Placa" value={veic.placa || c.veiculo_placa} mono highlight />
                                <Field label="Marca / Modelo" value={[veic.marca || c.veiculo_marca, veic.modelo || c.veiculo_modelo].filter(Boolean).join(' ')} />
                                <Field label="Ano Fabricação" value={veic.ano_fabricacao} />
                                <Field label="Cor" value={veic.cor} />
                                <Field label="Combustível" value={veic.combustivel} />
                                <Field label="Tipo" value={veic.tipo_veiculo} />
                                <Field label="Espécie" value={veic.especie} />
                                <Field label="RENAVAM" value={veic.renavam} mono />
                                {veic.valor_fipe && <Field label="Valor FIPE" value={formatCurrency(veic.valor_fipe)} />}
                            </div>
                        </Section>
                    )}

                    {/* ── Cobranças ── */}
                    <Section title="Cobranças" icon={Receipt}
                        badge={<span className="text-[10px] bg-amber-100 text-amber-700 font-bold px-2 py-0.5 rounded-full">Asaas em breve</span>}>
                        <div className="rounded-2xl border border-dashed border-surface-200 bg-surface-50 p-6 text-center">
                            <CreditCard size={32} className="text-surface-300 mx-auto mb-3" />
                            <p className="text-sm font-medium text-surface-700/60">Integração com Asaas será implementada em breve</p>
                            <p className="text-xs text-surface-700/40 mt-1">As cobranças vinculadas a este contrato aparecerão aqui</p>
                            {c.asaas_subscription_id && (
                                <p className="text-xs font-mono text-primary-600 mt-3 bg-primary-50 px-3 py-1.5 rounded-lg inline-block">
                                    Sub: {c.asaas_subscription_id}
                                </p>
                            )}
                        </div>
                    </Section>

                </div>

                {/* Rodapé */}
                <div className="sticky bottom-0 bg-white/80 backdrop-blur-md px-6 py-4 border-t border-surface-100 flex gap-3">
                    <button onClick={onClose} className="btn btn-secondary flex-1">Fechar</button>
                    {c.status === 'ativo' && (
                        <button className="btn btn-primary flex-1 gap-2">
                            <Receipt size={16} /> Gerar Fatura Avulsa
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

// ── Sub-componentes ──────────────────────────────────────────

function Section({ title, icon: Icon, children, badge }: {
    title: string; icon: any; children: React.ReactNode; badge?: React.ReactNode
}) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 rounded-lg bg-primary-100 text-primary-600">
                    <Icon size={14} />
                </div>
                <p className="text-xs font-bold text-surface-700/50 uppercase tracking-widest">{title}</p>
                {badge}
            </div>
            <div className="bg-surface-50/50 rounded-2xl border border-surface-100 p-4">
                {children}
            </div>
        </div>
    )
}

function FinCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
    return (
        <div className={`rounded-xl p-3 text-center border ${highlight ? 'bg-primary-50 border-primary-200' : 'bg-white border-surface-100'}`}>
            <p className={`text-base font-bold ${highlight ? 'text-primary-700' : 'text-surface-900'}`}>{value}</p>
            <p className="text-[10px] text-surface-700/50 uppercase font-bold mt-0.5">{label}</p>
        </div>
    )
}

function Field({ label, value, mono, highlight, span, icon: Icon }: {
    label: string; value?: string | number | null; mono?: boolean
    highlight?: boolean; span?: number; icon?: any
}) {
    if (!value && value !== 0) return null
    return (
        <div className={span === 2 ? 'col-span-2' : ''}>
            <p className="text-[10px] font-bold text-surface-700/40 uppercase tracking-wider mb-0.5">{label}</p>
            <p className={`text-sm font-medium break-all ${mono ? 'font-mono' : ''} ${highlight ? 'text-primary-600' : 'text-surface-900'}`}>
                {Icon && <Icon size={12} className="inline mr-1 text-surface-400" />}
                {String(value)}
            </p>
        </div>
    )
}
